import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Styled from "./ReorderContent.styled";
import { FullScreenModal, getLayoutImage, HeaderLogo, BlockWithInfo, IPropsBasicModal, LayoutTemplates, TemplateBlockType, OrderedBlockItem } from "../../../libs";
import { createTheme, ThemeProvider } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { playlistsService, templateService, userService } from "../../../services";
import { Loader } from "../../ui";
import DragRows from "../../../assets/images/icons/icon-drag-rows.svg";
import SearchIcon from "../../../assets/images/icons/search.svg";
import { toast } from "react-toastify";
import { EyeFill, UiChecksGrid, XLg } from "react-bootstrap-icons";
import { ManageContentBlocks } from "./manage-content-blocks";

// Keeping it here as no need in other places


export const ReorderContent: React.FC<IPropsBasicModal> = ({ show, onHide, onOpenPreview }) => {
    const [blocks, setBlocks] = useState<BlockWithInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [inputValue, setInputValue] = useState<string>("");
    const [searchText, setSearchText] = useState("");
    const [originalBlocks, setOriginalBlocks] = useState<BlockWithInfo[]>([]);
    const [showManageContentBlocks, setShowManageContentBlocks] = useState(false);

    const fetchBlocks = useCallback(async () => {
        setLoading(true);
        try {
            const data = await playlistsService.getAllPlaylists();
            const liveStreamTemplateId = await userService.getUserKVStoreKey("liveStreamLayout");
            const vodGeneralTemplateId = await userService.getUserKVStoreKey("general_vod_template");
            const vodFilmFestivalTemplateId = await userService.getUserKVStoreKey("film_festival_vod_template");

            const dynamicBlocks = data.filter((item) => item.visibility !== 1).map(({ playlistId, title, templateId, description }) => ({
                blockId: playlistId,
                title,
                subtitle: description || "No description available",
                svgSrc: getLayoutImage(templateId ?? LayoutTemplates.Layout1),
                blockType: TemplateBlockType.PLAYLIST
            }));

            const staticBlocks = [
                {
                    blockId: "1",
                    title: "General Video on Demand",
                    subtitle: "All the videos you have stored in the VOD folder will be displayed here.",
                    svgSrc: getLayoutImage(vodGeneralTemplateId?.value || LayoutTemplates.Layout1),
                    blockType: TemplateBlockType.VOD_GENERAL
                },
                {
                    blockId: "2",
                    title: "Film Festival Video on Demand",
                    subtitle: "All the videos you have stored in the VOD folder will be displayed here.",
                    svgSrc: getLayoutImage(vodFilmFestivalTemplateId?.value || LayoutTemplates.Layout1),
                    blockType: TemplateBlockType.VOD_FILM_FESTIVAL
                },
                {
                    blockId: "3",
                    title: "Streamings",
                    subtitle: "All the videos you have stored in the VOD folder will be displayed here.",
                    svgSrc: getLayoutImage(liveStreamTemplateId?.value || LayoutTemplates.Layout1),
                    blockType: TemplateBlockType.LIVESTREAM
                },
            ];
            console.log("Setting blocks:", [...dynamicBlocks, ...staticBlocks]);
            setBlocks([...dynamicBlocks, ...staticBlocks]);
            setOriginalBlocks([...dynamicBlocks, ...staticBlocks]);
        } catch (error) {
            console.error("Error fetching blocks:", error);
        } finally {
            setLoading(false);
        }
    }, []);


    const UpdateTemplateOrder = useCallback(async (data: any) => {
        setLoading(true);
        try {
            const response = await templateService.updateTemplateOrder(data);
            if (response.error) {
                toast.error(response.error);
            } else {
                toast.success("Template order updated successfully.");
                onHide();
            }
        } catch (error: any) {
            const backendMessage = error?.response?.data?.error || error?.response?.error || "Failed to update template order.";
            toast.error(backendMessage);
            console.error("Error updating template order:", error);
        } finally {
            setLoading(false);
        }
    }, [onHide]);

    const hasFetchedTemplate = useRef(false);

    useEffect(() => {
        if (show && !hasFetchedTemplate.current) {
            hasFetchedTemplate.current = true;
            fetchBlocks();
        } else if (!show) {
            setSearchText("");
            hasFetchedTemplate.current = false;
        }
    }, [show, fetchBlocks]);

    useEffect(() => {
        if (show) {
            // Update blocks only if there was a change, otherwise do nothing
            if (JSON.stringify(blocks) !== JSON.stringify(originalBlocks)) {
                setBlocks([...originalBlocks]);
            }
        }
    }, [show, originalBlocks, blocks]);

    const [data, setData] = useState<BlockWithInfo[]>([]);

    useEffect(() => {
        const applyTemplateOrder = async () => {
            try {
                setLoading(true);
                const templateOrder = await templateService.getTemplateOrder();

                // Blocks already have the correct blockType assigned during fetch

                const isEqualData = (a: any, b: OrderedBlockItem[]) => {
                    if (a.length !== b.length) return false;
                    for (let i = 0; i < a.length; i++) {
                        if (
                            a[i].blockId !== b[i].blockId ||
                            a[i].orderNumber !== b[i].orderNumber ||
                            a[i].blockType !== b[i].blockType
                        ) {
                            return false;
                        }
                    }
                    return true;
                };

                const orderedData: OrderedBlockItem[] = templateOrder.data.order
                    .map(order => {
                        const matchedItem = blocks.find(
                            b => String(b.blockId).trim() === String(order.blockId).trim()
                        );
                        return matchedItem
                            ? { ...matchedItem, orderNumber: order.orderNumber }
                            : null;
                    })
                    .filter((x): x is OrderedBlockItem => x !== null);

                // Prevent redundant updates
                if (!isEqualData(data, orderedData)) {
                    let final = orderedData.map((ele, i) => ({
                        ...ele,
                        numb: i
                    }));

                    setData(final);
                }
            } catch (error) {
                console.error("Error fetching template order:", error);
            } finally {
                setLoading(false);
            }
        };

        if (blocks.length > 0 && !data.length) {
            applyTemplateOrder();
        }
    }, [blocks, data]);

    const columns = React.useMemo<MRT_ColumnDef<BlockWithInfo>[]>(() => [
        // Drag handle column (MUST be first or second)
        {
            header: "#",
            Cell: ({ row }) => {
                const currentIndex = row.original.numb ?? 0;

                const isValidIndex = (val) => {
                    const num = parseInt(val);
                    return !isNaN(num) && num > 0 && num <= data.length;
                };

                const handleInputBlurOrEnter = (fromEnter = false) => {
                    const newIndex = parseInt(inputValue) - 1;

                    if (!inputValue.trim()) {
                        setEditingIndex(null);
                        return;
                    }

                    if (isNaN(newIndex) || newIndex < 0 || newIndex >= data.length) {
                        if (fromEnter) {
                            toast.error(`Please enter a number between 1 and ${data.length}`);
                        }
                        return;
                    }

                    if (newIndex !== currentIndex) {
                        const updatedData = [...data];
                        const [movedItem] = updatedData.splice(currentIndex, 1);
                        updatedData.splice(newIndex, 0, movedItem);

                        const reindexedData = updatedData.map((item, index) => ({
                            ...item,
                            numb: index,
                        }));
                        setData(reindexedData);
                    }

                    setEditingIndex(null);
                };

                return editingIndex === currentIndex ? (
                    <Styled.StyledNumberInput
                        type="number"
                        value={inputValue}
                        isValid={isValidIndex(inputValue) || !inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onBlur={() => handleInputBlurOrEnter(false)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleInputBlurOrEnter(true);
                        }}
                        autoFocus
                    />
                ) : (
                    <Styled.ClickableIndex
                        onClick={() => {
                            setEditingIndex(currentIndex);
                            setInputValue((currentIndex + 1).toString());
                        }}
                    >
                        {currentIndex + 1}
                    </Styled.ClickableIndex>
                );
            },
            size: 20,
        },
        {
            header: "Content Block",
            accessorKey: "title",
            Cell: ({ row }) => {
                const { svgSrc, title, subtitle } = row.original;
                return (
                    <Styled.TitleCellWrapper>
                        <Styled.Thumbnail src={svgSrc} alt="Thumbnail" />
                        <Styled.TextContent>
                            <Styled.Title>{title}</Styled.Title>
                            <Styled.Subtitle>{subtitle}</Styled.Subtitle>
                        </Styled.TextContent>
                    </Styled.TitleCellWrapper>
                );
            },
            size: 300,
        },
    ], [editingIndex, inputValue, data]);

    const filteredData = React.useMemo(() => {
        return data.filter(item =>
            item.title?.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [searchText, data]);

    const table = useMaterialReactTable({
        columns,
        data: filteredData,
        enableRowOrdering: true,
        enableFilters: false,
        enableSorting: true,
        enablePagination: true,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,

        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 10,
            },
        },
        paginationDisplayMode: 'pages',
        renderTopToolbar: '',
        positionPagination: "bottom",
        muiPaginationProps: {
            showRowsPerPage: true,
            showFirstButton: true,
            showLastButton: true,
        },
        muiTableHeadProps: {
            sx: {
                display: 'none',
            },
        },
        muiRowDragHandleProps: ({ table }) => ({
            onDragEnd: () => {
                const { draggingRow, hoveredRow } = table.getState();
                const oldIndex = draggingRow?.index;
                const newIndex = hoveredRow?.index;

                if (typeof oldIndex === 'number' && typeof newIndex === 'number') {
                    const updated = [...data];
                    const [moved] = updated.splice(oldIndex, 1);
                    updated.splice(newIndex, 0, moved);
                    setData(updated);
                }
            },
            style: { cursor: 'grab' },
        }),
        icons: {
            DragHandleIcon: () => {
                return (
                    <img
                        src={DragRows}
                        alt="Drag Rows"
                        style={{ zIndex: "-1" }}
                    />
                );
            },
        },
        mrtTheme: {
            baseBackgroundColor: "#0F1013",
            draggingBorderColor: "#b6f84d",
        },
    });

    const theme = createTheme({ palette: { mode: "dark" } }); const SaveData = () => {
        let payload = data.map((ele, i) => {
            return {
                orderNumber: i + 1,
                blockId: ele.blockId,
                blockType: ele.blockType
            };
        });

        UpdateTemplateOrder({
            order: payload
        })
        setOriginalBlocks([...blocks]);
    }

    const updateOrder = useCallback((order: BlockWithInfo[]) => {
        const updatedData = order.map((item, index) => ({
            ...item,
            numb: index
        }));
        setData(updatedData);
    }, []);

    return (
        <FullScreenModal
            show={show}
            onHide={onHide}
            header={
                <Styled.ModalHeader closeButton closeVariant="white">
                    <HeaderLogo />
                </Styled.ModalHeader>
            }
            body={
                loading ? (
                    <Loader />
                ) : (
                    <>
                        <Styled.ModalBodyWrapper>
                            <Styled.StyledContainer>
                                <Styled.Header>
                                    <Styled.LeftSection>Manage Library</Styled.LeftSection>
                                    <Styled.RightSection>
                                        <Styled.CustomButton variant="outline-secondary" onClick={() => showManageContentBlocks ? setShowManageContentBlocks(false) : onHide()}>
                                            <Styled.IconWrapper>
                                                <XLg />
                                            </Styled.IconWrapper>
                                            <Styled.Label>
                                                Close
                                            </Styled.Label>
                                        </Styled.CustomButton>
                                        <Styled.CustomButton variant="outline-secondary" onClick={() => setShowManageContentBlocks((val) => !val)} longLabel={true}>
                                            <Styled.IconWrapper>
                                                <UiChecksGrid />
                                            </Styled.IconWrapper>
                                            <Styled.Label>
                                                Manage Content
                                            </Styled.Label>
                                        </Styled.CustomButton>
                                        <Styled.CustomButton variant="outline-secondary" onClick={onOpenPreview} longLabel={true}>
                                            <Styled.IconWrapper>
                                                <EyeFill />
                                            </Styled.IconWrapper>
                                            <Styled.Label>
                                                Preview
                                            </Styled.Label>
                                        </Styled.CustomButton>
                                        <Styled.SaveButton onClick={() => {
                                            showManageContentBlocks ? setShowManageContentBlocks(false) : SaveData();
                                        }} variant="primary">Save</Styled.SaveButton>
                                    </Styled.RightSection>
                                </Styled.Header>

                                <Styled.Description>
                                    Here you can organize all the content to see how it will appear on your page.
                                    <Styled.DashboardSearchWrapper>
                                        <Styled.SearchBarWrapper>
                                            <Styled.SearchIcon src={SearchIcon} alt="Search" />
                                            <Styled.SearchInput
                                                type="text"
                                                placeholder="Search..."
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                            />
                                        </Styled.SearchBarWrapper>
                                    </Styled.DashboardSearchWrapper>
                                </Styled.Description>

                                {
                                    showManageContentBlocks ? (
                                        <ManageContentBlocks
                                            order={data}
                                            blocks={blocks}
                                            updateOrder={updateOrder}
                                        />
                                    ) : (<ThemeProvider theme={theme}>
                                        <MaterialReactTable table={table} />
                                    </ThemeProvider>)
                                }
                            </Styled.StyledContainer>
                        </Styled.ModalBodyWrapper>
                    </>
                )
            }
        />
    );

};
